import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { DataService } from '../data.service';
import {
  SubscriptionAction,
  UserSubscriptionStatus,
} from '../shared/enums/subscription';
import { LocalStorageKeys } from '../shared/LocalStorageKeys';
import { Destroyable } from '../shared/services/destroyable';
import { DialogService } from '../shared/services/dialog.service';
import { AccountProfileService } from '../shared/services/evergent/account/account-profile.service';
import { UserEntitlementService } from '../shared/services/evergent/entitlement/user-entitlement.service';
import { ContactMessageModel } from '../shared/services/evergent/onboarding/model/get-profiles/get-contact-response.model';
import { NavigationLibraryService } from '../shared/services/navigation/navigation.service';
import { LocalStorageService } from '../shared/services/storage/local-storage.service';
import { GenericAhaGoldPromptComponent } from '../shared/shared-components/aha-upgrade-prompts/generic/generic-aha-gold-prompt/generic-aha-gold-prompt.component';
import { MyAhaService } from './my-aha.service';
import { KEYS } from '../shared/utils/constants/storage.constants';
import { NewrelicService } from '../analytics/newrelic/services/newrelic.service';
import { AuthService } from '../shared/services/auth/auth.service';
import { StorefrontService } from '../storefront/storefront.service';
import { MetaDataService } from '../shared/services/meta-data.service';

@Component({
  selector: 'app-my-aha',
  templateUrl: './my-aha.component.html',
  styleUrls: ['./my-aha.component.scss'],
})
export class MyAhaComponent extends Destroyable implements OnInit, OnDestroy {
  hasPersonalizedContent: boolean | null = null;
  selectedContentLanguage: string;
  profileData: ContactMessageModel[] = [];
  selectedProfile: ContactMessageModel;
  planPrefix: string;
  planName: string;
  maskedMobileNumber: string;
  maskedEmail: string;
  userPlanStatus: 'none' | 'gold' | 'basic' = 'basic';
  showPlanAction: SubscriptionAction | null = null;
  isCancelledUser: boolean = false;
  isFromLanguageSelection: boolean = false;
  isFromSettings: boolean = false;
  isFromSubscribeOrUpgrade: boolean = false;
  saved: boolean = false;
  state: string = null;

  constructor(
    private router: Router,
    private readonly myAhaService: MyAhaService,
    private readonly dialogService: DialogService,
    private readonly accountProfileService: AccountProfileService,
    private readonly userEntlmtService: UserEntitlementService,
    private readonly navService: NavigationLibraryService,
    private readonly userEntitlementService: UserEntitlementService,
    private readonly dataService: DataService,
    private readonly localStorage: LocalStorageService,
    private readonly newrelicService: NewrelicService,
    private readonly authService: AuthService,
    private readonly storefrontService: StorefrontService,
    private readonly metaDataService: MetaDataService
  ) {
    super();
    const language = this.myAhaService.getLanguage();
    if (language) {
      this.selectedContentLanguage = language;
    }
  }

  get selectedProfileIndex() {
    return this.selectedProfile?.index !== undefined &&
      this.selectedProfile?.index !== null
      ? this.selectedProfile?.index.toLowerCase() !== 'kids'
        ? parseInt(this.selectedProfile?.index)
        : this.profileData.length - 1
      : 0;
  }

  ngOnInit(): void {
    this.isCancelledUser =
      this.userEntitlementService?.userSubscriptionStatus() ===
      UserSubscriptionStatus.CANCELLED;
    if (this.dataService.forceOpenMyAha) {
      this.dataService.forceOpenMyAha = false;
      this.dialogService.closeAll();
    }
    this.myAhaService.clearData();
    this.initialiseObserver();
    this.loadPage();
    const contacts = this.accountProfileService.contacts;
    if (contacts?.length) {
      this.selectedProfile = this.accountProfileService.selectedProfile;
      const sortedContact = contacts
        .filter((it) => it.index.toLowerCase() !== 'kids')
        .sort((a, b) => parseInt(a.index) - parseInt(b.index));
      const kidsContact = contacts.find(
        (it) => it.index.toLowerCase() === 'kids'
      );
      this.profileData = [...sortedContact, kidsContact];
    }

    this.userEntitlementService.planActionBtn$
      .pipe(takeUntil(this.destroy$))
      .subscribe((action) => {
        this.showPlanAction = action;
      });

    this.navService.focusContainer('top-panel', true, 0, false);
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  async loadPage() {
    // Set loading state
    this.hasPersonalizedContent = null;

    // Fetch data
    await this.myAhaService.fetchPageData();

    this.planPrefix = this.userEntlmtService.isSubscribed
      ? 'active_plan_title'
      : 'no_active_plan';
    this.planName = this.userEntlmtService.subscriptionName;
    this.maskedMobileNumber =
      this.accountProfileService.maskedMobileNumberHtml();
    this.maskedEmail = this.accountProfileService.maskedEmailHtml();
  }

  getTranslatedSubscriptionName() {
    return this.showPlanAction === SubscriptionAction.UPGRADE
      ? 'upgrade'
      : 'subscribe';
  }

  //called by reuse
  async reload(fullReload: boolean) {
    if (fullReload) {
      await this.loadPage();
      // Reset navigation focus to main container after data is loaded
      this.navService.setIndexContainer('horizontal-carousel-cards-0', 0);
      this.navService.focusContainer(
        'horizontal-carousel-cards-0',
        false,
        0,
        false
      );
      return;
    }

    const handleTopPanelFocus = (
      item: 'upgrade' | 'subscribe' | 'language' | 'settings'
    ) => {
      this.navService.focusContainer(
        'top-panel',
        true,
        this.getItemIndex(item),
        false
      );
    };

    const focusActions = {
      subscribe: () => handleTopPanelFocus('subscribe'),
      language: () => handleTopPanelFocus('language'),
      settings: () => handleTopPanelFocus('settings'),
      default: () => {
        this.navService.focusContainer(
          `horizontal-carousel-cards-${this.dataService.verticalIndex}`,
          true,
          this.dataService.horizontalIndex
        );
      },
    };

    if (this.dataService.isSubscribeOrUpgradeClicked) {
      focusActions.subscribe();
      this.dataService.isSubscribeOrUpgradeClicked = false;
    } else if (this.dataService.isLanguageClicked) {
      focusActions.language();
      this.dataService.isLanguageClicked = false;
    } else if (this.dataService.isSettingsClicked) {
      focusActions.settings();
      this.dataService.isSettingsClicked = false;
    } else {
      focusActions.default();
    }
  }

  getItemIndex(itemName: 'upgrade' | 'subscribe' | 'language' | 'settings') {
    const lastProfileIndex = this.profileData.length - 1;
    const hasPlanAction = this.showPlanAction !== null;

    switch (itemName) {
      case 'upgrade':
        return lastProfileIndex + 1;
      case 'subscribe':
        return lastProfileIndex + 1;
      case 'language':
        return lastProfileIndex + (hasPlanAction ? 2 : 1);
      case 'settings':
        return lastProfileIndex + (hasPlanAction ? 3 : 2);
    }
  }

  onSubcriptionClick() {
    this.dataService.isSubscribeOrUpgradeClicked = true;
    this.localStorage.setItem(LocalStorageKeys.TAB_CHANGED, 'false');
    if (this.showPlanAction === SubscriptionAction.UPGRADE) {
      this.dialogService
        .openDialogWithResult(GenericAhaGoldPromptComponent, {
          data: {
            buttonText: 'go_back',
          },
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.setIndexAndFocus('top-panel', this.profileData.length);
        });
    } else {
      this.router.navigate(['plans']);
    }
  }

  onSettingsClick() {
    this.dataService.isSettingsClicked = true;
    this.localStorage.setItem(LocalStorageKeys.TAB_CHANGED, 'false');
    this.router.navigate([
      '/settings',
      {
        queryParams: {
          isFromSettings: true,
        },
      },
    ]);
  }

  async onProfileClick(profile: ContactMessageModel) {
    try {
      // await this.entitlementService.fetchEntitlements(profile.contactID);
      this.localStorage.setItem(KEYS.FRESH_LAUNCH, 'true');
      this.dataService.emitVerticalMenuEvent({
        action: 'show',
        data: {
          reload: true,
          tabId: this.dataService.homeTab,
          shouldFocus: true,
        },
      });
      this.selectedProfile = profile;
      this.accountProfileService.selectedProfile = profile;
      this.localStorage.setItem(KEYS.FRESH_LAUNCH, 'true');
      this.dataService.clearVerticalMenuLastEvent();
      this.authService.resetPlayback();
      await this.authService.initPlatformService();
      await this.authService.contentAuthorizer.registerDevice();
      this.router.navigate(['/']);
    } catch (error) {
      this.newrelicService.sendErrorEvent({
        error,
      });
      return;
    }
  }

  initialiseObserver() {
    this.myAhaService.hasData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((hasData) => {
        this.hasPersonalizedContent = hasData;
        this.handleFocus();
      });
  }

  private handleFocus() {
    if (
      window.history.state &&
      this.dataService.myAhaLastFocusedIndex !== null
    ) {
      this.setIndexAndFocus(
        'top-panel',
        this.dataService.myAhaLastFocusedIndex
      );
      this.dataService.myAhaLastFocusedIndex = null;
    } else {
      if (this.hasPersonalizedContent) {
        this.navService.focusContainer('main-container', false, 0, true);
      } else {
        this.navService.focusContainer('explore-container', false, 0, false);
      }
    }
  }

  private setIndexAndFocus(containerName: string, index: number) {
    this.navService.setIndexContainer(containerName, index);
    this.navService.focusContainer(containerName, true, index, true);
  }

  // Handle keydown events
  onKeyDown(event: KeyboardEvent) {
    if (event.isDown()) {
      this.navService.focusContainer('explore-container', false, 0, false);
    } else if (event.isLeft()) {
      this.onArrowLeft();
    }
  }

  onArrowLeft() {
    const index = this.navService.getHorizontalIndex();
    if (index === 0) {
      this.dataService.emitVerticalMenuEvent({
        action: 'open',
      });
    }
  }

  trackByIndex(index: number): number {
    return index;
  }
}
